(ns ui.view.grid-opensearch.sub
  (:require-macros [ui.subs.macros :refer [<-1]])
  (:require
   [clojure.string :as string]
   [common.localization :as loc]
   [common.opensearch.fields :as os-fields]
   [common.opensearch.utils :as os-utils]
   [common.search.capabilities :as search-capabilities]
   [common.schema.data-types :as data-types]
   [common.schema.listing-definition :as listing-definition]
   [common.schema.registry :as registry]
   [common.schema.system :as schema-system]
   [frontend.logging :as logging]
   [frontend.state.current-session :as session]
   [ui.view.grid-opensearch.custom-renderers :as custom-renderers]
   [oops.core :as oops]
   [re-frame.core :as rf]
   [ui.state.session.sub :as session-sub]
   [ui.view.grid-opensearch.event :as event]
   [ui.view.grid-opensearch.model :as model]
   [ui.view.grid-opensearch.utils :as utils]
   [ui.view.opensearch-value.currency :as currency]))

;; Root grid state for a given grid-id
(rf/reg-sub
 ::grid-state
 (fn [db [_ grid-id]]
   (let [full-path (model/grid-path grid-id)
         grid-data (get-in db full-path)]
     grid-data)))

;; Entity type used by this grid instance
(rf/reg-sub
 ::entity-type
 (<-1 ::grid-state)
 (fn [grid-state [_ _grid-id]]
   (let [resp (get grid-state model/entity-type-key)]
     resp)))

;; Saved filters for this grid (not the ag-grid filter-model)
(rf/reg-sub
 ::filters
 (<-1 ::grid-state)
 (fn [grid-state _]
   (get grid-state model/filters-key)))

;; Pipeline id for pre-configured searches (optional)
(rf/reg-sub
 ::pipeline-id
 (<-1 ::grid-state)
 (fn [grid-state _]
   (get grid-state model/pipeline-key)))

;; Grid UI configuration (column order, sorts, sizing, etc.)
(rf/reg-sub
 ::config
 (<-1 ::grid-state)
 (fn [grid-state [_ _grid-id]]
   (let [resp (get grid-state model/config-key)]
     resp)))

;; Sort model stored in config or computed by grid events
(rf/reg-sub
 ::os-sort
 (<-1 ::config)
 (fn [config _]
   (get config :sorted-by)))

;; Filter model from ag-grid (client-side filter UI)
(rf/reg-sub
 ::filter-model
 (<-1 ::grid-state)
 (fn [grid-state _]
   (get grid-state model/filters-key)))

(defn localize-header
  [field-path]
  (let [tenant-schema @session/tenant-schema
        field-label (fn [field]
                      (loc/field-name field
                                      (registry/get-field-schema tenant-schema field)))]
    (cond
      (keyword? field-path)
      (field-label field-path)

      (sequential? field-path)
      (let [field-path (vec field-path)]
        (if (< 1 (count field-path))
          (str (field-label (first field-path))
               " - "
               (field-label (last field-path)))
          (localize-header (last field-path))))

      :else
      (do (logging/error "Unexpected grid field - cannot localize header " field-path)
          (pr-str field-path)))))

(defn data-type->ag-filter
  [data-type]
  (let [filter-type (:default-filter (get data-types/zapflow-data-types data-type))]
    (case filter-type
      :filter.type/text "agTextColumnFilter"
      :filter.type/boolean "agSetColumnFilter"
      :filter.type/date "agDateColumnFilter"
      :filter.type/select "agSetColumnFilter"
      :filter.type/number "agNumberColumnFilter"
      :filter.type/integer "agNumberColumnFilter"
      :filter.type/percentage "agNumberColumnFilter"
      :filter.type/multiplier "agNumberColumnFilter"
      :filter.type/file "agSetColumnFilter"
      :filter.type/none "agSetColumnFilter"
      "agTextColumnFilter")))

(defn sortable-data-type?
  [data-type]
  (loop [current data-type
         seen #{}]
    (let [capabilities (get search-capabilities/default-capabilities-by-type current)]
      (cond
        (map? capabilities)
        (true? (:search/sortable? capabilities))

        (and (keyword? capabilities)
             (not (contains? seen capabilities)))
        (recur capabilities (conj seen current))

        :else
        false))))

(defn sortable-column?
  "A column is sortable only when its value exists under `flat.*`/`labels.*`.

   `data-type` comes from the field's schema entry, so a nil data-type means the
   field is not in the entity schema at all - and materialization is
   schema-driven, so nothing was ever written for it. Sorting such a column
   silently returns an arbitrary order rather than erroring. See
   ai/opensearch-document-model.md."
  [field-path data-type]
  (and (some? data-type)
       (sortable-data-type? data-type)
       (not (schema-system/hidden-system-field? field-path))
       (not (contains? #{:common/migration-note
                         :common/system-kws}
                       field-path))))

(defn- column-display-field
  [field-path data-type]
  (if (contains? os-fields/ref-data-types data-type)
    (os-utils/field-path->labels-alias field-path)
    (os-utils/field-path->flat-alias field-path)))

(defn- source-field-value
  [data source-field]
  (cond
    (string/starts-with? source-field "labels.")
    (get-in data ["labels" (subs source-field (count "labels."))])

    (string/starts-with? source-field "flat.")
    (get-in data ["flat" (subs source-field (count "flat."))])

    (string/starts-with? source-field "original.")
    (get-in data ["original" (subs source-field (count "original."))])

    :else
    (get data source-field)))

(defn- false-like-bool-value?
  [value]
  (cond
    (nil? value) true
    (false? value) true
    (number? value) (zero? value)
    (keyword? value) (= value :false)
    (string? value) (contains? #{"0" "false" "FALSE" "False" ""} value)
    :else false))

(defn- facet-bool-value
  [value]
  (not (false-like-bool-value? value)))

(rf/reg-sub
 ::default-currency
 (fn [_ _]
   (rf/subscribe [::session-sub/tenant]))
 (fn [tenant _]
   (:tenant/default-currency tenant)))

(rf/reg-sub
 ::date-format
 (fn [_ _]
   (rf/subscribe [::session-sub/tenant]))
 (fn [tenant _]
   (or (:tenant/date-format tenant)
       (loc/tr :date-format))))

(rf/reg-sub
 ::ag-grid-col-defs
 #_(<-1m ::config ::entity-type)
 (fn [query-v _]
   (let [[_ grid-id] query-v]
     (js/console.log "HERE : " grid-id " << " query-v)
     [(rf/subscribe [::config grid-id])
      (rf/subscribe [::entity-type grid-id])
      (rf/subscribe [::default-currency])
      (rf/subscribe [::date-format])]))
 (fn [[config entity-type default-currency date-format] [_ grid-id]]
    ;; Expect config to contain a vector of column configs under :columns.
    ;; Each column config is a map like:
    ;; {:id           :screening-profile/first-name
    ;;  :field        "flat.first-name"
    ;;  :header-label "First name"
    ;;  :width        180
    ;;  :pinned       "left"
    ;;  :sortable?    true
    ;;  :filter?      true
    ;;  :ag-props     {...optional extra ag-grid props...}}
   (let [{:keys [columns hidden-columns]} config
         pinned-left (set (listing-definition/grid-pinned-field-paths entity-type :left))
         pinned-right (set (listing-definition/grid-pinned-field-paths entity-type :right))
         mandatory-columns (set (listing-definition/grid-mandatory-field-paths entity-type))
         columns (->> (or columns
                          (do (println "NO COLUMNS : " config)
                              []))
                      (filter (fn [field-path]
                                (not (schema-system/hidden-system-field? field-path))))
                      vec)
         sort-props (event/sort-column-props (:sorted-by config) columns)
         name-column (->> columns
                          (keep (fn [col]
                                  (let [field-schema (registry/get-field-schema @session/tenant-schema
                                                                                (os-fields/field-path->last-kw col))]
                                    (when (= (:field/attribute field-schema)
                                             (:field/name-attribute field-schema))
                                      col))))
                          first)
          ;; Ensure we always have exactly one selection owner column.
          ;; If schema-level name marker is missing for a listing, fall back to first configured column.
         selected-name-column (or name-column
                                  (first columns))]
     (->> columns
          (mapv (fn [col]
                  (let [field-schema (registry/get-field-schema @session/tenant-schema (os-fields/field-path->last-kw col))
                        data-type (:field/data-type field-schema)
                        name-column? (= (:field/attribute field-schema)
                                        (:field/name-attribute field-schema))
                        selected-name-column? (= col selected-name-column)
                        mandatory-column? (contains? mandatory-columns col)
                        pinned-side (cond
                                      selected-name-column? "left"
                                      (contains? pinned-left col) "left"
                                      (contains? pinned-right col) "right"
                                      :else nil)
                        sortable? (sortable-column? col data-type)
                        field (os-utils/field-path->alias col)
                        flat-field (os-utils/field-path->flat-alias col)
                        label-field (os-utils/field-path->labels-alias col)
                        display-field (column-display-field col data-type)
                        header-label (localize-header col)
                        filter-type (data-type->ag-filter data-type)
                        currency-field-path (currency/currency-field-path {:field-path   col
                                                                           :field-schema field-schema})
                        currency-flat-field (some-> currency-field-path os-utils/field-path->flat-alias)]
                    (merge
                     {:headerName            header-label
                      :hide                  (if mandatory-column?
                                               false
                                               (if selected-name-column?
                                                 false
                                                 (boolean (get hidden-columns col))))
                      :pinned                pinned-side
                      :lockPinned            (boolean pinned-side)
                      :suppressMovable       (or mandatory-column?
                                                 selected-name-column?)
                      :lockPosition          (when (or mandatory-column?
                                                       selected-name-column?)
                                               (or pinned-side true))
                      :lockVisible           (or mandatory-column?
                                                 selected-name-column?)
                      :sortable              sortable?
                      :name-column?          name-column?
                      :field                 display-field
                      :field-path            col
                      :field-data-type       data-type
                      :field-str             field
                      :flat-field            flat-field
                      :label-field           label-field
                      :display-field         display-field
                      :field-path-str        (pr-str col)
                      :currency-field-path   currency-field-path
                      :currency-flat-field   currency-flat-field
                      :colId                 field
                      :suppressMenu          false
                      :filter                filter-type
                      :floatingFilter        false
                      :checkboxSelection     selected-name-column?
                      :headerCheckboxSelection selected-name-column?
                      :valueGetter           (fn [params-js]
                                               (let [data (js->clj (aget params-js "data"))
                                                     value (if (contains? os-fields/labelled-data-types data-type)
                                                             (or (source-field-value data label-field)
                                                                 (source-field-value data flat-field))
                                                             (source-field-value data display-field))]
                                                 value))
                      :valueFormatter        (fn [params-js]
                                               (let [value (utils/col-value-formatter {:data-type           data-type
                                                                                       :default-currency    default-currency
                                                                                       :currency-flat-field currency-flat-field
                                                                                       :date-format         date-format
                                                                                       :field-schema        field-schema} params-js)]
                                                 value))

                      :equals                (fn [value1 value2]
                                               (= (js->clj value1)
                                                  (js->clj value2)))
                      :suppressKeyboardEvent (fn [event]
                                               (let [keyboard-event (oops/oget event "event")
                                                     _key-str (str (oops/oget keyboard-event "code"))
                                                     event (js->clj event :keywordize-keys true)
                                                     _editing? (:editing event)
                                                     _event-key (get-in event [:event :code])]
                                                 #_(cond
                                                     (and editing? (= key-str "Enter"))
                                                     (do
                                                       (js/console.log "suppress event")
                                                       true)

                                                     :else
                                                     false)

                                                 false))}
                     ;; The saved sort of the config, applied once, when the column is created.
                     (get sort-props col)
                     (when (= filter-type "agSetColumnFilter")
                       {:filterParams {:refreshValuesOnOpen true
                                         ;; We return complex objects for set filter values:
                                         ;; {:value <stable value> :label <human label> :count <doc_count>}
                                         ;; AG Grid needs keyCreator + valueFormatter for these.
                                       :keyCreator          (fn [p]
                                                              (let [v (oops/oget p "value")]
                                                                (oops/oget v "value")))
                                       :valueFormatter      (fn [params-js]
                                                              (let [params (js->clj params-js :keywordize-keys true)
                                                                    item (:value params)
                                                                    {:keys [value count label]} item]
                                                                (str (or label value)
                                                                     " (" count ")")))
                                       :values              (fn [params]
                                                                ;; Call backend with field + entity-type
                                                              (rf/dispatch
                                                               [::event/get-filter-values
                                                                {:field         col
                                                                 :grid-id       grid-id
                                                                 :entity-type   entity-type
                                                                 :on-success-fn (fn [values]
                                                                                  (js/console.log "FACET VALUES CALLBACK" values "\n" params)
                                                                                  (let [values-js (->> (or values [])
                                                                                                       (mapv (fn [{:keys [value label count]}]
                                                                                                               #js {:value (cond (keyword? value)
                                                                                                                                 (pr-str value)
                                                                                                                                 (= data-type :data.type/boolean)
                                                                                                                                 (facet-bool-value value)
                                                                                                                                 :else
                                                                                                                                 value)
                                                                                                                    :label label
                                                                                                                    :count count}))
                                                                                                       (into-array))]
                                                                                    (oops/ocall params "success" values-js)))}]))}})

                     ;; A numeric column has its header and its cells on the right.
                     (utils/numeric-alignment-props data-type)
                     ;; A currency value has the label on the left, and the number on the right.
                     (when (= :data.type/currency-value data-type)
                       {:cellRenderer (custom-renderers/currency-cell-renderer
                                       {:default-currency    default-currency
                                        :field-schema        field-schema
                                        :currency-flat-field currency-flat-field})})
                     (when-let [custom-renderer (custom-renderers/get-field-cell-renderer field-schema)]
                       {:cellRenderer custom-renderer})))))))))

(rf/reg-sub
 ::accessible-field-paths
 (fn [db [_ grid-id]]
   (get-in db (conj (model/grid-path grid-id) model/accessible-field-paths-key))))

(rf/reg-sub
 ::fields
 (fn [[_ grid-id] _]
   [(rf/subscribe [::ag-grid-col-defs grid-id])
    (rf/subscribe [::accessible-field-paths grid-id])])
 (fn [[col-defs accessible-field-paths] _]
   (event/request-field-paths {:column-defs            col-defs
                               :accessible-field-paths accessible-field-paths})))

(rf/reg-sub
 ::os-fields
 (<-1 ::ag-grid-col-defs)
 (fn [col-defs _]
   (->> col-defs
        (mapv :field-path))))

(rf/reg-sub
 ::os-queries
 (<-1 ::os-fields)
 (fn [fields _]
   [{:multi_match {:fields    fields
                   :lenient   true
                   :query     ""
                   :fuzziness "AUTO"}}]))
